import { MoreOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, message, TableProps } from 'antd';
import dayjs from 'dayjs';
import React, { forwardRef, memo, useState } from 'react';
import { EinvoiceHistoryApis } from 'apis/EinvoiceHistoryApis';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { HocChangePagination } from 'utils/HocChangePagination';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { formatCurrency } from 'utils/constants/functions';
import DetailEinvoiceHistoryModal from './DetailEinvoiceHistoryModal';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const TableEinvoiceHistory = forwardRef((props: IProps, _ref: any) => {
  const { data = {}, loading, refetch } = props;
  const { user } = useUser();

  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [resending, setResending] = useState(false);

  const isAccountant = !!user?.isAccountant;

  const checkCanResend = (record: any) => {
    const isEmailValid =
      record?.emailSuccessCount === 0 && (record?.emailResendCount || 0) < 3;
    const isSmsValid =
      record?.smsSuccessCount === 0 && (record?.smsResendCount || 0) < 3;

    const canResendEmail =
      isAccountant || (record?.canResendEmail ?? isEmailValid);
    const canResendSms = isAccountant || (record?.canResendSms ?? isSmsValid);
    const canResendAll =
      isAccountant ||
      (record?.canResendAll ?? (canResendEmail && canResendSms));

    return { canResendEmail, canResendSms, canResendAll };
  };

  const handleResend = async (
    record: any,
    channel: 'EMAIL' | 'SMS' | 'ALL',
  ) => {
    const { canResendEmail, canResendSms, canResendAll } =
      checkCanResend(record);

    if (!isAccountant) {
      if (channel === 'EMAIL' && !canResendEmail) {
        message.warning(
          record?.emailSuccessCount > 0
            ? 'Hóa đơn đã được gửi thành công qua Email. Không thể gửi lại.'
            : 'Kênh Email đã vượt quá số lần gửi lại tối đa (3 lần).',
        );
        return;
      }
      if (channel === 'SMS' && !canResendSms) {
        message.warning(
          record?.smsSuccessCount > 0
            ? 'Hóa đơn đã được gửi thành công qua SMS. Không thể gửi lại.'
            : 'Kênh SMS đã vượt quá số lần gửi lại tối đa (3 lần).',
        );
        return;
      }
      if (channel === 'ALL' && !canResendAll) {
        message.warning(
          'Không đủ điều kiện gửi lại tất cả các kênh (kênh đã thành công hoặc đã gửi tối đa 3 lần).',
        );
        return;
      }
    }

    const channels = channel === 'ALL' ? ['EMAIL', 'SMS'] : [channel];
    const channelLabel =
      channel === 'ALL' ? 'Email & SMS' : channel === 'EMAIL' ? 'Email' : 'SMS';
    try {
      setResending(true);
      const res: any = await EinvoiceHistoryApis.resendEinvoice({
        soCode: record.soCode || record.invoiceCode,
        channels: channels,
        invoiceCode: record.invoiceCode,
        billNum: record.billNum,
        branchCode: record.branchCode,
        lookupUrl: record.lookupUrl || '',
        customerId: record.customerId,
        customerName: record.customerName,
        customerType: record.customerType,
        taxCode: record.taxCode,
        customerEmail: record.customerEmail,
        customerPhone: record.customerPhone,
      });

      const isSuccess = res?.success || res?.data?.success;
      if (isSuccess) {
        message.success(
          res?.message ||
            res?.data?.message ||
            `Yêu cầu gửi lại qua ${channelLabel} đã được tiếp nhận!`,
        );
        refetch();
      } else {
        message.error(
          res?.message ||
            res?.data?.message ||
            `Gửi lại qua ${channelLabel} thất bại`,
        );
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error.message || 'Có lỗi xảy ra',
      );
    } finally {
      setResending(false);
    }
  };

  const getActionMenuItems = (record: any): MenuProps['items'] => {
    const { canResendEmail, canResendSms, canResendAll } =
      checkCanResend(record);

    return [
      {
        key: 'detail',
        label: 'Xem chi tiết',
        onClick: () => {
          setSelectedRecord(record);
          setIsOpenDetailModal(true);
        },
      },
      {
        key: 'resend_email',
        label: 'Gửi Email HĐ',
        disabled: !canResendEmail,
        onClick: () => handleResend(record, 'EMAIL'),
      },
      {
        key: 'resend_sms',
        label: 'Gửi SMS HĐ',
        disabled: !canResendSms,
        onClick: () => handleResend(record, 'SMS'),
      },
      {
        key: 'resend_all',
        label: 'Gửi lại tất cả',
        disabled: !canResendAll,
        onClick: () => handleResend(record, 'ALL'),
      },
    ];
  };

  const columns: ColumnsTypeCustom = [
    {
      title: 'Chi Nhánh',
      width: 100,
      dataIndex: 'branchCode',
      align: 'center',
      render: (val: string) => val || '-',
    },
    {
      title: 'Mã Bill',
      width: 130,
      dataIndex: 'billNum',
      render: (val: string, record: any) =>
        val ||
        record?.billNum ||
        record?.logs?.find((l: any) => l.billNum && l.billNum !== '-')
          ?.billNum ||
        '-',
    },
    {
      title: 'Số HĐ',
      width: 90,
      dataIndex: 'invoiceNumber',
      align: 'center',
      render: (val: string, record: any) =>
        val ||
        record?.logs?.find(
          (l: any) => l.invoiceNumber && l.invoiceNumber !== '-',
        )?.invoiceNumber ||
        '-',
    },
    {
      title: 'Ký Hiệu',
      width: 110,
      dataIndex: 'invoiceSerial',
      align: 'center',
      render: (val: string, record: any) =>
        val ||
        record?.logs?.find(
          (l: any) => l.invoiceSerial && l.invoiceSerial !== '-',
        )?.invoiceSerial ||
        '-',
    },
    {
      title: 'Ngày Phát Hành',
      width: 130,
      dataIndex: 'publishDate',
      align: 'center',
      render: (val: string, record: any) => {
        const d =
          val || record?.logs?.find((l: any) => l.publishDate)?.publishDate;
        return d ? dayjs(d).format('DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Giá Trị HĐ',
      width: 140,
      dataIndex: 'invoiceAmount',
      align: 'right',
      render: (val: number) => (val ? formatCurrency({ value: val }) : '0 đ'),
    },
    {
      title: 'Mã KH',
      width: 130,
      dataIndex: 'customerId',
      render: (val: string) => val || '-',
    },
    {
      title: 'Tên KH',
      width: 160,
      dataIndex: 'customerName',
      render: (val: string) => val || '-',
    },
    {
      title: 'Loại KH',
      width: 130,
      dataIndex: 'customerType',
      render: (val: string) => val || '-',
    },
    {
      title: 'Số Lần Gửi SMS Thành Công',
      width: 150,
      dataIndex: 'smsSuccessCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Số Lần Gửi Email Thành Công',
      width: 150,
      dataIndex: 'emailSuccessCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Ngày Gửi Thành Công',
      width: 140,
      dataIndex: 'successTime',
      align: 'center',
      render: (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Người Gửi',
      width: 140,
      dataIndex: 'successUser',
      render: (val: string, record: any) =>
        val ||
        (record?.smsSuccessCount > 0 || record?.emailSuccessCount > 0
          ? record?.userFullName
          : '-'),
    },
    {
      title: 'Số Lần Gửi SMS Thất Bại',
      width: 140,
      dataIndex: 'smsFailedCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Số Lần Gửi Email Thất Bại',
      width: 140,
      dataIndex: 'emailFailedCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Ngày Gửi Thất Bại',
      width: 140,
      dataIndex: 'failedTime',
      align: 'center',
      render: (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Người Gửi',
      width: 140,
      dataIndex: 'failedUser',
      render: (val: string, record: any) =>
        val ||
        (record?.emailFailedCount > 0 || record?.smsFailedCount > 0
          ? record?.userFullName
          : '-'),
    },
    {
      title: 'Tác Vụ',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record: any) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={['click']}
        >
          <button className="cursor-pointer p-1 hover:bg-gray-100 rounded">
            <MoreOutlined className="text-lg rotate-90" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <Content>
      <TableComponent
        columns={columns}
        dataSource={data.items || []}
        loading={loading || resending}
        bordered
        rowKey="soCode"
        scroll={{ x: 2600 }}
        pagination={{
          current: data?.currentPage || data?.page || 1,
          pageSize: data?.pageSize || data?.limit || DEFAULT_PAGE_SIZE,
          total: data?.total || 0,
          onChange: HocChangePagination(),
        }}
      />

      {isOpenDetailModal && (
        <DetailEinvoiceHistoryModal
          isOpen={isOpenDetailModal}
          onClose={() => setIsOpenDetailModal(false)}
          record={selectedRecord}
        />
      )}
    </Content>
  );
});

export default memo(TableEinvoiceHistory);
